import { ComponentProps, useEffect, useState } from "react";

import {
  CurrentUserWithAccountsDocument,
  DeleteAccountFeedback,
  StripeSubscriptionStatus,
  useDeleteAccountMutation,
  useStripeSubscriptionQuery,
} from "@/apollo/types";
import { PrimaryButton, SecondaryButton } from "@/components/elements/Button";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import { TextArea } from "@/components/primitives/input/TextArea";
import { useToast } from "@/providers/ToastProvider";
import { useCurrentAccount } from "@/providers/account";
import { workspacesRoute } from "@/router";
import { RadioGroup } from "@headlessui/react";
import { useNavigate } from "@tanstack/react-location";

const feedbackOptions = [
  {
    value: DeleteAccountFeedback.TooExpensive,
    label: "It's too expensive",
  },
  {
    value: DeleteAccountFeedback.MissingFeatures,
    label: "Lacking some essential features",
  },
  {
    value: DeleteAccountFeedback.SwitchedService,
    label: "I found a better service",
  },
  {
    value: DeleteAccountFeedback.Unused,
    label: "I don't use the service enough",
  },
  {
    value: DeleteAccountFeedback.CustomerService,
    label: "Customer service was less than expected",
  },
  {
    value: DeleteAccountFeedback.TooComplex,
    label: "Ease of use was less than expected",
  },
  {
    value: DeleteAccountFeedback.LowQuality,
    label: "Too many technical problems",
  },
  {
    value: DeleteAccountFeedback.Other,
    label: "Other reason",
  },
];

function useVerifyAccountDeletion() {
  const currentAccount = useCurrentAccount();
  const { data, loading } = useStripeSubscriptionQuery({
    fetchPolicy: "network-only",
    variables: {
      slug: currentAccount.slug,
    },
  });

  const subscription =
    data?.accountBySlug.stripeSubscription?.stripeSubscriptionDetails;

  return {
    isPending: loading,
    isDeletable:
      subscription == null ||
      subscription.status === StripeSubscriptionStatus.Canceled ||
      subscription.status === StripeSubscriptionStatus.IncompleteExpired ||
      subscription.status === StripeSubscriptionStatus.Trialing,
  };
}

export function DeleteAccountDialog(
  props: Omit<ComponentProps<typeof Modal>, "children">,
) {
  return (
    <Modal {...props}>
      <ModalHeader>Delete account</ModalHeader>
      <ModalCloseButton />
      <DeleteAccountDialogContent {...props} />
    </Modal>
  );
}

function DeleteAccountDialogContent(props: ComponentProps<typeof Modal>) {
  const currentAccount = useCurrentAccount();

  const [feedback, setFeedback] = useState<DeleteAccountFeedback | null>(null);
  const [comment, setComment] = useState<string>("");

  const toast = useToast();
  const navigate = useNavigate();

  const verificationResult = useVerifyAccountDeletion();

  useEffect(() => {
    if (verificationResult.isDeletable) {
      return;
    }

    toast(
      "Cannot delete account",
      "Please cancel your active subscription before deleting your account.",
      "error",
    );
  }, [verificationResult.isDeletable, toast]);

  const [deleteAccount, { loading: isDeletingAccount }] =
    useDeleteAccountMutation({
      onError(error: { message: string }) {
        toast(
          `${currentAccount.name ?? "Your account"} was not deleted`,
          error.message,
          "error",
        );
      },
      refetchQueries: [CurrentUserWithAccountsDocument],
      onCompleted() {
        navigate({ to: workspacesRoute() });
      },
    });

  return (
    <>
      <ModalBody className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 text-sm">
          <p>
            If confirmed, your account will be deleted immediately. An invoice
            will be generated for any outstanding charges.
          </p>
          <p>
            Please note that you will need to cancel any active subscriptions
            before deleting your account.
          </p>
          <p>
            We're sorry to see you go! In our continuous effort to enhance our
            services,{" "}
            <span className="font-semibold">
              could you please share more details about your decision to leave?
            </span>{" "}
            Your valuable feedback will help us better understand your needs and
            improve our offerings for the future.
          </p>
        </div>
        <div className="">
          <RadioGroup value={feedback} onChange={setFeedback}>
            <div className="flex flex-col gap-2">
              {feedbackOptions.map((option) => (
                <RadioGroup.Option value={option.value} key={option.value}>
                  {({ checked }) => (
                    <div key={option.value} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={option.value}
                        checked={checked}
                        className="form-radio"
                        readOnly
                      />
                      <RadioGroup.Label>{option.label}</RadioGroup.Label>
                    </div>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>
        </div>
        <div>
          <TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Please share any feedback you have"
          />
        </div>
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <PrimaryButton
          colorScheme="danger"
          onClick={() => {
            if (
              window.confirm("Are you sure you want to delete your account?")
            ) {
              deleteAccount({
                variables: {
                  input: {
                    id: currentAccount.id,
                    deletionDetails: {
                      feedback,
                      comment,
                    },
                  },
                },
              });
            }
          }}
          isDisabled={
            feedback == null || verificationResult.isDeletable === false
          }
          isLoading={isDeletingAccount || verificationResult.isPending}
          loadingText={
            verificationResult.isPending ? "Verifying..." : "Deleting ..."
          }
        >
          Delete my account
        </PrimaryButton>
        <SecondaryButton onClick={() => props.onClose()}>
          Cancel
        </SecondaryButton>
      </ModalFooter>
    </>
  );
}
